import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useAdminConfirm } from '../shared/AdminConfirmProvider';
import { isUserActive, useSetUserActiveMutation } from './api/usersApi';

/**
 * Deactivating a user or activating them again, after the admin confirms, for the users
 * table's Active switch and the user drawer. Deactivating stops them signing in (and ends
 * their sessions); their deals, tasks and history stay. Admins can't deactivate themselves.
 *
 * @param {object} user a users list item
 * @returns {{
 *   isActive: boolean,
 *   canChange: boolean, false for the admin's own record
 *   isLoading: boolean,
 *   setActive: (isActive: boolean) => Promise<void>,
 * }}
 */
export const useSetUserActive = (user) => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const [setUserActive, { isLoading }] = useSetUserActiveMutation();
	const email = useSelector((state) => state.msalAccount.account?.username);
	const { data: currentUser } = useGetUserDetailsQuery({ email }, { skip: !email });

	const setActive = async (isActive) => {
		const confirmed = await confirm({
			title: `${isActive ? 'Activate' : 'Deactivate'} ${user.name}?`,
			content: isActive
				? 'They will be able to sign in to Folio again.'
				: 'They will be signed out and will no longer be able to sign in. Their deals, tasks and history stay as they are.',
			confirmText: isActive ? 'Activate' : 'Deactivate',
		});
		if (!confirmed) return;

		const result = await setUserActive({ id: user.id, isActive });
		if (result.error) {
			dispatch(
				showToast({
					message: `Couldn't ${isActive ? 'activate' : 'deactivate'} ${user.name}. Please try again.`,
					error: true,
				}),
			);
		}
	};

	return {
		isActive: isUserActive(user),
		canChange: currentUser?.userId !== user.id,
		isLoading,
		setActive,
	};
};
